import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Badge } from "../../../../../components/ui/badge";
import Sheet from "../../../../../components/shared/Sheet";
import Button from "../../../../../components/shared/Button";
import Avatar from "../../../../../components/shared/Avatar";
import { useCompanyCurrency, useCompanyDateFormat, useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { renderUserName } from "../../../../../utils/helpers/user";
import { renderCurrencySymbol } from "../../../../../utils/helpers/job";
import { STATUS_CLASSNAMES, getQualifications } from "../hooks/useColumns";
import { useQualificationPreferences } from "../../../Component/hooks/useQualificationPreferences";
import { cn } from "../../../../../lib/utils";

function Detail({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
            <span className="tw-text-sm">{children || "—"}</span>
        </div>
    );
}

function ViewSheet({ selected, visible, close, approve, deny }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const currency = useCompanyCurrency();
    const qualifications = useQualificationPreferences();
    const onApprove = useCallback(() => {
        approve(selected);
        close();
    }, [approve, selected, close]);

    const onDeny = useCallback(() => {
        deny(selected);
        close();
    }, [deny, selected, close]);

    const data = selected?.data;
    const schedule = data?.schedule;
    const section = schedule?.sections?.[0];
    const user = data?.originalUser || selected?.user;
    const date = schedule?.date || data?.startDate;
    const startDate = schedule?.startDate || data?.startDate;
    const endDate = schedule?.endDate || data?.endDate;
    const createdAt = selected?.createdAt;
    const extraPayment = data?.extraPayment;
    const hasExtraPay = extraPayment?.payAmount != null && extraPayment?.payAmount !== "";
    const isPending = selected?.status === "pending";
    const assignedUsers: any[] = data?.assignedUsers || selected?.assignedUsers || [];

    const renderList = (values?: any[]) => values?.length
        ? values.map(renderSettingsResourceLabel).join(", ")
        : null;

    return (
        <Sheet
            title={t("request-details")}
            open={visible}
            close={close}
            footer={(isPending && selected?.type !== "direct-offer" && selected?.requiredToBeApprovedBy?.length > 0) && (
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-w-full">
                    <Button type="button" variant="ghost" onClick={close}>
                        {t("cancel")}
                    </Button>
                    <Button type="button" variant="destructive" onClick={onDeny}>
                        {t("deny")}
                    </Button>
                    <Button type="button" onClick={onApprove}>
                        {t("approve")}
                    </Button>
                </div>
            )}
        >
            {selected && (
                <div className="tw-flex tw-flex-col tw-gap-6">
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <Avatar user={user} size="lg" />
                            <div className="tw-flex tw-flex-col">
                                <span className="tw-font-semibold">{user ? renderUserName(user) : "—"}</span>
                                <span className="tw-text-sm tw-text-muted-foreground">{t(selected?.type || "open-shift")}</span>
                            </div>
                        </div>
                        {selected?.status && (
                            <Badge variant="outline" className={cn(STATUS_CLASSNAMES[selected.status])}>
                                {t(selected.status)}
                            </Badge>
                        )}
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-rounded-lg tw-border tw-p-4">
                        <Detail label={t("date")}>
                            {date && moment.parseZone(date).format(dateFormat)}
                        </Detail>
                        <Detail label={t("time")}>
                            {startDate && endDate && `${moment.parseZone(startDate).format(timeFormat)} - ${moment.parseZone(endDate).format(timeFormat)}`}
                        </Detail>
                        <Detail label={t("job")}>
                            {renderSettingsResourceLabel(section?.job || data?.job)}
                        </Detail>
                        <Detail label={t("location")}>
                            {renderSettingsResourceLabel(section?.location || data?.location)}
                        </Detail>
                        <Detail label={t("extra-pay")}>
                            {hasExtraPay && (
                                <>
                                    {`${renderCurrencySymbol(currency)}${extraPayment.payAmount} `}
                                    <span className="tw-text-muted-foreground">
                                        ({t(extraPayment.payType === "hourly" ? "per-hour" : "flat-amount").toLowerCase()})
                                    </span>
                                </>
                            )}
                        </Detail>
                        <Detail label={t("taken-by")}>
                            {data?.takeUser && renderUserName(data.takeUser)}
                        </Detail>
                        <Detail label={t("created-at")}>
                            {createdAt && moment(createdAt).format(`${dateFormat} ${timeFormat}`)}
                        </Detail>
                    </div>

                    {(qualifications.skills || qualifications.licenses || qualifications.certifications) && (
                        <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-rounded-lg tw-border tw-p-4">
                            {qualifications.skills && (
                                <Detail label={t("skills")}>{renderList(getQualifications(selected, "skills"))}</Detail>
                            )}
                            {qualifications.licenses && (
                                <Detail label={t("licenses")}>{renderList(getQualifications(selected, "licenses"))}</Detail>
                            )}
                            {qualifications.certifications && (
                                <Detail label={t("certifications")}>{renderList(getQualifications(selected, "certifications"))}</Detail>
                            )}
                        </div>
                    )}

                    {assignedUsers.length > 0 && (
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <span className="tw-text-xs tw-text-muted-foreground">{t("assigned-users")}</span>
                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-text-sm">
                                {assignedUsers.map((assignedUser) => (
                                    <div
                                        key={assignedUser?.id}
                                        className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-py-1 tw-pl-1 tw-pr-3"
                                    >
                                        <Avatar user={assignedUser} size="sm" />
                                        <span>{renderUserName(assignedUser)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <Detail label={t("reason")}>
                        {data?.reason && <span className="tw-whitespace-pre-wrap">{data.reason}</span>}
                    </Detail>
                </div>
            )}
        </Sheet>
    );
}

export default ViewSheet;